import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator } from "lucide-react";
import { grossToNet, netToGross, formatMKD, RATES } from "@/lib/mk-salary";
import { apiGetCompanyCurrency } from "@/lib/api";

function QuickCalculator({ t }: { t: any }) {
  const [amount, setAmount] = useState("");
  const [inputType, setInputType] = useState<"gross" | "net">("gross");
  const [result, setResult] = useState<ReturnType<typeof grossToNet> | null>(
    null,
  );

  const { data: currencyData } = useQuery({
    queryKey: ["company-currency"],
    queryFn: apiGetCompanyCurrency,
  });
  const currency = currencyData?.currency ?? "MKD";

  useEffect(() => {
    const val = parseInt(amount);
    if (isNaN(val) || val <= 0) {
      setResult(null);
      return;
    }
    setResult(inputType === "gross" ? grossToNet(val) : netToGross(val));
  }, [amount, inputType]);

  return (
    <Card className="shadow-card animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Calculator className="h-4 w-4 text-primary" />
          {t("payroll.calculator")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <select
            value={inputType}
            onChange={(e) => setInputType(e.target.value as "gross" | "net")}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="gross">{t("payroll.inputGross")}</option>
            <option value="net">{t("payroll.inputNet")}</option>
          </select>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t("payroll.enterAmount")}
            className="h-10 flex-1 max-w-xs rounded-md border border-input bg-background px-3 text-sm tabular-nums"
          />
          <span className="text-sm text-muted-foreground font-medium">{currency}</span>
        </div>

        {result && (
          <div className="border rounded-lg overflow-hidden animate-in fade-in duration-300">
            <table className="w-full text-sm">
              <tbody>
                {[
                  {
                    label: t("payroll.gross"),
                    value: result.grossSalary,
                    bold: true,
                  },
                  {
                    label: `${t("payroll.pension")} (${(RATES.pension * 100).toFixed(1)}%)`,
                    value: result.pensionContribution,
                  },
                  {
                    label: `${t("payroll.health")} (${(RATES.health * 100).toFixed(1)}%)`,
                    value: result.healthContribution,
                  },
                  {
                    label: `${t("payroll.unemployment")} (${(RATES.unemployment * 100).toFixed(1)}%)`,
                    value: result.unemploymentContribution,
                  },
                  {
                    label: `${t("payroll.sickness")} (${(RATES.sickness * 100).toFixed(1)}%)`,
                    value: result.sicknessContribution,
                  },
                  {
                    label: t("payroll.contributions"),
                    value: result.totalContributions,
                    bold: true,
                  },
                  {
                    label: t("payroll.grossMinusContributions"),
                    value: result.grossMinusContributions,
                  },
                  {
                    label: t("payroll.personalExemption"),
                    value: result.personalExemption,
                  },
                  { label: t("payroll.taxBase"), value: result.taxBase },
                  {
                    label: t("payroll.incomeTax"),
                    value: result.personalIncomeTax,
                  },
                  {
                    label: t("payroll.deductions"),
                    value: result.totalDeductions,
                    bold: true,
                  },
                  {
                    label: t("payroll.net"),
                    value: result.netSalary,
                    bold: true,
                    highlight: true,
                  },
                ].map((row, i) => (
                  <tr
                    key={i}
                    className={`border-b last:border-0 ${row.highlight ? "bg-primary/5" : i % 2 === 0 ? "bg-muted/30" : ""}`}
                  >
                    <td
                      className={`px-4 py-2 ${row.bold ? "font-semibold" : ""} ${row.highlight ? "text-primary font-bold" : ""}`}
                    >
                      {row.label}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums w-32 ${row.bold ? "font-semibold" : ""} ${row.highlight ? "text-primary font-bold" : ""}`}
                    >
                      {formatMKD(row.value)}
                    </td>
                    <td className="px-2 py-2 text-right text-xs text-muted-foreground w-10">
                      {currency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { QuickCalculator };
